import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ArrowUpRight, X, Award, FileCheck2, ScanLine } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import { MaskedRevealOnView, FadeUp } from './motion/Reveal';

const ISO_IMAGE = 'https://customer-assets-7cd3h4nn.emergentagent.net/job_bright-steel-pro/artifacts/py1y9nvw_IMG_2866.jpeg';

const ISO = {
    code: 'ISO 9001 : 2015',
    label: 'Quality Management Systems',
    number: 'E20250728188',
    issued: '14 / 07 / 2025',
    valid: '13 / 07 / 2028',
    scope: 'Manufacturing and supply of cold drawn, peeled & centreless ground alloy steel bars.',
    body: 'Royal Assessments Pvt. Ltd. · IAF · EGAC accredited',
    verifyUrl: 'https://www.iafcertsearch.org/',
};

const SUPPORTS = [
    {
        icon: FileCheck2,
        title: 'Mill Test Certificate',
        code: 'EN 10204 · 3.1',
        body: 'Every consignment ships with a certified MTC — heat number, chemistry, mechanicals and dimensions on paper.',
    },
    {
        icon: ScanLine,
        title: 'Full Traceability',
        code: 'Heat · Batch · Bar',
        body: 'From billet to bundle — every bar is stamped, logged and traceable to its heat number and inspection record.',
    },
    {
        icon: Award,
        title: 'In-house QA Lab',
        code: 'Dimensional · UT · ECT',
        body: '100% dimensional, ultrasonic and eddy-current testing — no bar leaves the plant without a signature.',
    },
];

const Quality = () => {
    const [open, setOpen] = useState(false);

    // ESC-to-close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [open]);

    return (
        <section id="quality" data-testid="quality-section" className="bg-[#0A0A0B] text-white py-28 md:py-40 border-t border-white/10 relative overflow-hidden">
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />
            <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-16">
                    <div className="col-span-12 md:col-span-4">
                        <span className="overline text-[#FF3B00]">Quality · 04</span>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <MaskedRevealOnView
                            className="h-display text-[10vw] md:text-[6vw] leading-[0.9]"
                            lines={['Certified.', 'Audited. Repeatable.']}
                        />
                        <FadeUp delay={0.15}>
                            <p className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
                                Every consignment ships with a Mill Test Certificate. Our QA lab runs dimensional, chemical, mechanical and NDT verification on every heat.
                            </p>
                        </FadeUp>
                    </div>
                </div>

                {/* Bento — real certificate + supporting cards */}
                <div className="grid grid-cols-12 gap-4 md:gap-5 border-t border-white/15 pt-10">
                    {/* Featured ISO certificate */}
                    <FadeUp className="col-span-12 lg:col-span-7">
                        <div className="relative border border-white/15 bg-[#111112] p-6 md:p-8 clip-corner h-full flex flex-col">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="overline text-[#FF3B00] mb-2">Verified · Live</div>
                                    <div className="h-display text-4xl md:text-5xl">{ISO.code}</div>
                                    <div className="overline text-white/60 mt-3">{ISO.label}</div>
                                </div>
                                <div className="flex items-center gap-2 border border-[#FF3B00] px-3 py-1.5">
                                    <span className="w-1.5 h-1.5 bg-[#FF3B00] rounded-full animate-pulse" />
                                    <span className="overline text-[#FF3B00]">Active</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-6 flex-1">
                                {/* Thumbnail */}
                                <button
                                    onClick={() => setOpen(true)}
                                    data-testid="view-iso-certificate"
                                    className="col-span-12 sm:col-span-5 relative group overflow-hidden bg-white border border-white/15"
                                    aria-label="View ISO 9001:2015 certificate"
                                >
                                    <img
                                        src={ISO_IMAGE}
                                        alt="ISO 9001:2015 Certificate"
                                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                                        style={{ aspectRatio: '3/4' }}
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500 grid place-items-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[#FF3B00] text-white px-4 py-2 overline">
                                            View certificate
                                        </div>
                                    </div>
                                </button>

                                {/* Details */}
                                <div className="col-span-12 sm:col-span-7 flex flex-col justify-between">
                                    <div>
                                        <div className="grid grid-cols-2 border-t border-white/15">
                                            <Row label="Cert No." value={ISO.number} />
                                            <Row label="Issued" value={ISO.issued} />
                                            <Row label="Valid until" value={ISO.valid} />
                                            <Row label="Body" value="RAPL · IAF · EGAC" />
                                        </div>
                                        <div className="mt-6">
                                            <div className="overline text-white/50 mb-2">Scope</div>
                                            <p className="text-white/85 text-sm md:text-[15px] leading-relaxed">
                                                {ISO.scope}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-6 flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={() => setOpen(true)}
                                            data-testid="view-certificate-cta"
                                            className="btn-invert inline-flex items-center gap-2 h-12 px-5 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.22em] hover:text-[#FF3B00]"
                                        >
                                            <ShieldCheck size={14} strokeWidth={2} />
                                            View certificate
                                        </button>
                                        <a
                                            href={ISO.verifyUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="verify-iaf"
                                            className="inline-flex items-center gap-2 h-12 px-5 border border-white/25 text-white text-[11px] font-mono uppercase tracking-[0.22em] hover:border-white hover:bg-white hover:text-[#0A0A0B] transition-colors"
                                        >
                                            Verify · IAF
                                            <ArrowUpRight size={14} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FadeUp>

                    {/* Supporting practices */}
                    <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-4 md:gap-5">
                        {SUPPORTS.map((s, i) => (
                            <FadeUp key={s.title} delay={0.1 + i * 0.08}>
                                <div className="border border-white/15 bg-[#111112] p-6 md:p-7 clip-notch flex gap-5 items-start hover:border-[#FF3B00]/60 transition-colors">
                                    <s.icon className="text-[#FF3B00] mt-1 flex-shrink-0" size={26} strokeWidth={1.4} />
                                    <div>
                                        <div className="overline text-white/50 mb-1">{s.code}</div>
                                        <div className="h-display text-xl md:text-2xl mb-2">{s.title}</div>
                                        <p className="text-white/70 text-sm leading-relaxed">{s.body}</p>
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                {/* Outlined marquee */}
                <div className="mt-24 marquee-wrap">
                    <Marquee gradient={false} speed={30} direction="right">
                        {['Traceable', 'Repeatable', 'Certified', 'On-spec'].map((w, i) => (
                            <span key={i} className="mx-10 flex items-center gap-10">
                                <span
                                    className="h-display text-[60px] md:text-[110px] text-transparent"
                                    style={{ WebkitTextStroke: '1px rgba(255,255,255,0.7)' }}
                                >
                                    {w}
                                </span>
                                <span className="text-[#FF3B00] text-4xl md:text-6xl">/</span>
                            </span>
                        ))}
                    </Marquee>
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        onClick={() => setOpen(false)}
                        data-testid="certificate-lightbox"
                        className="fixed inset-0 z-[80] bg-[#0A0A0B]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.96, y: 20 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-[900px] w-full max-h-[92vh] bg-white shadow-[0_30px_100px_-20px_rgba(0,0,0,0.9)] overflow-hidden cursor-default"
                        >
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="close-lightbox"
                                aria-label="Close certificate"
                                className="absolute top-3 right-3 z-10 h-11 w-11 grid place-items-center bg-[#0A0A0B] text-white hover:bg-[#FF3B00] transition-colors"
                            >
                                <X size={18} />
                            </button>
                            <img
                                src={ISO_IMAGE}
                                alt="ISO 9001:2015 Certificate · Full"
                                className="w-full h-auto max-h-[92vh] object-contain block"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

const Row = ({ label, value }) => (
    <div className="p-3 border-b border-white/15 [&:nth-child(odd)]:border-r [&:nth-child(odd)]:border-white/15">
        <div className="overline text-white/45 mb-1">{label}</div>
        <div className="font-mono text-sm text-white">{value}</div>
    </div>
);

export default Quality;
